import { Controller } from '@hotwired/stimulus'

// Ключ localStorage: { [slug]: количество завершённых прохождений }. Не
// история попыток — только счётчик, нужен «Пройти другой квиз», чтобы
// предлагать ещё не пройденный квиз или пройденный реже всего.
const COMPLETIONS_STORAGE_KEY = 'padelQuizCompletions'

// Падел-квиз: показывает вопросы по одному, считает верные ответы на клиенте
// и в конце открывает карточку результата с кнопками шейринга. Прохождения
// квиза нигде на сервере не хранятся — квиз можно проходить сколько угодно
// раз, только счётчик по слагам живёт в localStorage этого браузера.
export default class QuizController extends Controller {
  static targets = [
    'quiz',
    'question',
    'step',
    'bar',
    'next',
    'result',
    'resultCard',
    'score',
    'shareNative',
    'shareTg',
    'shareWa',
    'shareX',
    'shareCopy',
    'anotherQuiz'
  ]

  connect() {
    this.reset()

    // Остальные видимые квизы (см. QuizzesController#show) — JSON, не пусто
    // при отсутствующем атрибуте: массив по умолчанию упрощает pickNextQuiz.
    try {
      this.otherQuizzes = JSON.parse(
        this.element.dataset.quizOtherQuizzes || '[]'
      )
    } catch {
      this.otherQuizzes = []
    }

    // Нативное системное меню «Поделиться» доступно не везде (в основном
    // мобильные браузеры) — первая иконка в ряду, скрыта по умолчанию и
    // появляется, только если у браузера есть navigator.share. Остальные
    // иконки (копия ссылки, Telegram, WhatsApp, X) остаются как были.
    this.canShareNatively = typeof navigator.share === 'function'
    if (this.canShareNatively) {
      this.shareNativeTargets.forEach((el) => {
        el.hidden = false
      })
    }

    // ?result=N открывает сразу карточку результата с таким счётом — по такой
    // ссылке приходят из шейринга. Диапазон — фактическое число вопросов ЭТОГО
    // квиза (this.questionTargets.length), а не хардкод: у разных квизов
    // разное число вопросов. Постороннее значение не совпало бы ни с одной
    // карточкой и оставило бы пустой экран, поэтому такой квиз просто
    // стартует с начала. Это просмотр чужого результата по ссылке, а не
    // собственное прохождение — в счётчик localStorage не попадает.
    const preview = new URLSearchParams(window.location.search).get('result')
    const max = this.questionTargets.length
    const parsed = Number(preview)
    if (
      preview !== null &&
      /^\d+$/.test(preview) &&
      parsed >= 0 &&
      parsed <= max
    ) {
      this.correct = parsed
      this.showResult()
    }
  }

  select(event) {
    const button = event.currentTarget
    this.selected = button
    const current = this.questionTargets[this.index]
    current.querySelectorAll('.quiz-option').forEach((option) => {
      option.classList.toggle('quiz-option--selected', option === button)
    })
    this.nextTarget.disabled = false
  }

  next() {
    if (!this.selected) return

    if (this.selected.dataset.quizCorrect === '1') this.correct += 1
    this.selected = null
    this.index += 1

    if (this.index >= this.questionTargets.length) {
      this.recordCompletion()
      this.pickAnotherQuiz()
      this.showResult()
      return
    }

    this.nextTarget.disabled = true
    this.showQuestion()
  }

  restart() {
    this.reset()
  }

  reset() {
    this.index = 0
    this.correct = 0
    this.selected = null
    this.resultTarget.hidden = true
    this.quizTarget.hidden = false
    this.nextTarget.disabled = true
    this.element
      .querySelectorAll('.quiz-option--selected')
      .forEach((option) => option.classList.remove('quiz-option--selected'))
    this.showQuestion()
  }

  showQuestion() {
    this.questionTargets.forEach((question, i) => {
      question.hidden = i !== this.index
    })
    this.stepTarget.textContent = this.index + 1
    this.barTarget.style.width = `${(this.index / this.questionTargets.length) * 100}%`

    const last = this.index === this.questionTargets.length - 1
    const label = this.nextTarget.querySelector('.btn__text')
    if (label) {
      label.textContent = last
        ? this.element.dataset.quizResultLabel
        : this.element.dataset.quizNextLabel
    }
  }

  showResult() {
    this.quizTarget.hidden = true
    this.resultTarget.hidden = false
    this.scoreTargets.forEach((el) => {
      el.textContent = this.correct
    })
    this.resultCardTargets.forEach((card) => {
      const min = Number(card.dataset.min)
      const max = Number(card.dataset.max)
      card.hidden = !(this.correct >= min && this.correct <= max)
    })
    this.applyShareLinks()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  applyShareLinks() {
    // При максимальном счёте — «А ты сможешь повторить?» вместо «лучше?».
    const isMaxScore = this.correct === this.questionTargets.length
    const template =
      (isMaxScore && this.element.dataset.quizShareTextMaxTemplate) ||
      this.element.dataset.quizShareTextTemplate ||
      ''
    const text = template.replace('%{correct}', this.correct)
    // Ссылка с result=N: её OG-превью — карточка результата с подписью
    // padelmesh.com, и она же делает Padelmesh в сообщении кликабельным.
    const url = `${window.location.origin}${window.location.pathname}?result=${this.correct}`
    const encodedText = encodeURIComponent(text)
    const encodedUrl = encodeURIComponent(url)

    // Кнопки лежат в каждой карточке результата — заполняем все копии
    this.shareTgTargets.forEach((a) => {
      a.href = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`
    })
    this.shareWaTargets.forEach((a) => {
      a.href = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`
    })
    this.shareXTargets.forEach((a) => {
      a.href = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
    })
    this.shareCopyTargets.forEach((button) => {
      button.dataset.copyTextValue = `${text} ${url}`
    })

    // Использует shareNative() ниже — там же текст и ссылка, что и у
    // кнопок-фолбэков, просто передаются через navigator.share, а не href.
    this.shareText = text
    this.shareUrl = url
  }

  shareNative() {
    if (!this.canShareNatively) return
    navigator.share({ text: this.shareText, url: this.shareUrl }).catch(() => {
      // Отмена системного меню пользователем (AbortError) — не ошибка,
      // остальные сбои показать нечем, кнопки-фолбэки всё равно скрыты.
    })
  }

  // Считаем прохождение завершённым только здесь — когда все вопросы даны и
  // мы реально дошли до конца, а не когда карточка результата открыта через
  // ?result=N (см. connect()) или после restart().
  recordCompletion() {
    const completions = this.readCompletions()
    const slug = this.element.dataset.quizSlug
    if (!slug) return

    completions[slug] = (completions[slug] || 0) + 1
    this.writeCompletions(completions)
  }

  // «Пройти другой квиз»: сначала квиз, который ещё не проходили, иначе —
  // пройденный меньше всего раз. При равенстве счётчиков выбор случайный.
  pickAnotherQuiz() {
    if (!this.hasAnotherQuizTarget || this.otherQuizzes.length === 0) return

    const completions = this.readCompletions()
    let minCount = Infinity
    let candidates = []
    this.otherQuizzes.forEach((quiz) => {
      const count = completions[quiz.slug] || 0
      if (count < minCount) {
        minCount = count
        candidates = [quiz]
      } else if (count === minCount) {
        candidates.push(quiz)
      }
    })

    const pick = candidates[Math.floor(Math.random() * candidates.length)]
    this.anotherQuizTarget.href = pick.path
  }

  readCompletions() {
    try {
      return (
        JSON.parse(window.localStorage.getItem(COMPLETIONS_STORAGE_KEY)) || {}
      )
    } catch {
      return {}
    }
  }

  writeCompletions(completions) {
    try {
      window.localStorage.setItem(
        COMPLETIONS_STORAGE_KEY,
        JSON.stringify(completions)
      )
    } catch {
      // Приватный режим/отключённое хранилище — счётчик просто не сохранится.
    }
  }
}
